import {
	useMemo, useState, useCallback, useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GRS } from '../stores/types/types';
import Socket from '../connection/socket';
import { ConnectedUser } from '../stores/types/connection';
import { BACKSTAGE_COMMS_KEY_STATES } from '../stores/reducers/session';
import * as SessionActions from '../stores/actions/session';
import { VideoStream } from '../components/types/videos';
import { getNameOrInitials } from '../utils/utils';
import { setHiddenTitles, setSharingScreen } from '../stores/actions/video';
import type { Invitation } from '../stores/types/invitations';
import { setVideoTitlesHaveFired } from '../stores/actions/shots';
import { getUserWebRTCIdMap } from '../utils/user';
import { AVContext, useAVHasUnmutedAudio } from '../contexts/audio-video';
import { OpenTokContext } from '../contexts/opentok/opentok-context';
import { deleteCommToggle, setCommToggle } from '../connection/comm-toggle';
import useShots from '../contexts/shots/provider';
import { SocketContext } from '../contexts/socket';
import useProfile from './use-profile';

const useUserActions = () => {
	const dispatch = useDispatch();
	const backstageCommsKeyState = useSelector((s: GRS) => s.SessionReducer.backstageCommsKeyState);
	const webrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const connectedMediaVideos = useSelector((s: GRS) => s.VideoReducer.connectedMediaVideos);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const hiddenTitles = useSelector((s: GRS) => s.VideoReducer.hiddenTitles);
	const sharingScreen = useSelector((s: GRS) => s.VideoReducer.sharingScreen);
	const media = useSelector((s: GRS) => s.MediaReducer.media);
	const invitations = useSelector((s: GRS) => s.InvitationsReducer.invitations);
	const videoTitlesHaveFired = useSelector((s: GRS) => s.ShotsReducer.videoTitlesHaveFired);

	const {socket} = useContext(SocketContext);

	const { getAV, setAV, getAllAV } = useContext(AVContext);

	const anyAudio = useAVHasUnmutedAudio();

	const { audio, video } = getAV();

	const { opentok } = useContext(OpenTokContext);
	const { isOnCanvas, currentShot } = useShots();

	const connectedUserMap = useMemo(
		() => getUserWebRTCIdMap(connectedUsers),
		[connectedUsers, webrtcID],
	);

	const idsOfVideosInCanvas = useMemo(() => {
		const newIdsOfVideosInCanvas = [] as string[];
		currentShot.shot_data.shotFeeds.forEach((feed) => {
			if (feed.media) {
				const connectedVideoForMedia = connectedMediaVideos.find(
					(vid: VideoStream) => vid.mediaId === feed.media,
				);
				if (connectedVideoForMedia) newIdsOfVideosInCanvas.push(connectedVideoForMedia.id);
			}

			if (feed.presenter) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.profile === feed.presenter,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				} else {
					const restrictedVideoForPresenter = connectedUsers.find(
						(u: ConnectedUser) => u.gr_user === feed.presenter && !u.watcher_only,
					);
					if (restrictedVideoForPresenter?.socketId) {
						newIdsOfVideosInCanvas.push(
							`${restrictedVideoForPresenter.socketId}-restricted-user`,
						);
					}
				}
			}

			if (feed.sipStreamId) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.id === feed.sipStreamId,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}
		});
		return newIdsOfVideosInCanvas;
	}, [connectedPresenterVideos, connectedMediaVideos, connectedUsers, currentShot]);

	const backstageCommsToggle = useCallback((muted: boolean, targetUser: string = '') => {
		// if tagetUser is empty, then we are toggling all comms

		if (
			backstageCommsKeyState === BACKSTAGE_COMMS_KEY_STATES.ALL
			|| backstageCommsKeyState === BACKSTAGE_COMMS_KEY_STATES.INDIVIDUAL
			|| isOnCanvas
		) return;

		if (!meeting?.meeting || !user?.gr_user || !token) {
			return;
		}

		dispatch(SessionActions.setBackstageCommsTargetWebRtcId(muted ? '' : targetUser));

		if (muted) {
			dispatch(SessionActions.setBackstageCommsKeyState(BACKSTAGE_COMMS_KEY_STATES.CLICK));
			deleteCommToggle(meeting.meeting, token, user.gr_user);
		} else {
			dispatch(SessionActions.setBackstageCommsKeyState(BACKSTAGE_COMMS_KEY_STATES.OFF));
			setCommToggle(meeting.meeting, token, user.gr_user, targetUser, !targetUser);
		}

		const targetedPresenterID = targetUser || null;
		socket?.sendMessage('backstage-comms-toggle', { muted, producerID: webrtcID, targetedPresenterID });
	}, [meeting, user, token, backstageCommsKeyState, socket, webrtcID]);

	const titlesShowing = !videoTitlesHaveFired || hiddenTitles[0] !== 1;
	const toggleTitles = () => {
		// titles context sets hidden titles to [] when starting animation...
		if (titlesShowing) {
			socket?.sendMessage('toggle-all-titles', [[1], currentShot.shot_id]);
		} else {
			// if any have animated out, or if we are resetting the titles
			socket?.sendMessage('toggle-all-titles', [[0], currentShot.shot_id]);
		}
	};

	// We assume it is the local client muting their local state.
	// This is the case for all mute buttons in the GUI, except presenter feeds.
	// For presenter feeds, the id of this client is set to "local" as well.
	const toggleAudio = useCallback((reqWebrtcId: string = 'local') => {
		const idToMute = reqWebrtcId === webrtcID ? 'local' : reqWebrtcId;
		if (idToMute === 'local') {
			opentok?.setLocalAudio(!audio);
			setAV({ id: 'local', audio: !audio });
		} else {
			opentok?.forceSetAudio(idToMute, !getAV(idToMute).audio);
		}
	}, [getAV, setAV, webrtcID, audio]);

	const toggleVideo = useCallback((reqWebrtcId: string = 'local') => {
		const idToMute = reqWebrtcId === webrtcID ? 'local' : reqWebrtcId;
		if (idToMute === 'local') {
			opentok?.setLocalVideo(!video);
			setAV({ id: 'local', video: !video });
		} else {
			opentok?.forceSetVideo(idToMute, !getAV(idToMute).video);
		}
	}, [getAV, setAV, webrtcID, video]);

	const toggleMuteAll = useCallback(() => {
		const shouldUnmute = !anyAudio;
		const avIds = Object.keys(getAllAV());

		opentok?.setLocalAudio(shouldUnmute);
		avIds.forEach((id: string) => {
			if (id !== 'local') {
				opentok?.forceSetAudio(id, shouldUnmute);
			}
		});
	}, [getAllAV, anyAudio, opentok]);

	const {getMaskedProfileById} = useProfile({});

	const getFeedName = useCallback((vid: VideoStream): string => {
		if (vid.isMedia) {
			const mediaObject = media.find((med) => med.id === vid.mediaId);
			return (mediaObject?.custom_name ? mediaObject.custom_name : mediaObject?.original_name) || '';
		} if (vid.isSipCall && vid.displayName) {
			return vid.displayName;
		} if (!vid.isScreenShare && vid.srcObject) {
			if (vid.profile) {
				const maskedProfile = getMaskedProfileById(vid.profile);
				if (maskedProfile) {
					return getNameOrInitials(maskedProfile, true);
				}
			}
			const userObject = connectedUsers.find(
				(connectedUser) => connectedUser.gr_user === vid.profile,
			);
			if (userObject?.gr_user) {
				const maskedProfile = getMaskedProfileById(userObject?.gr_user);
				if (maskedProfile) {
					return getNameOrInitials(maskedProfile, true);
				}
			}
			return userObject ? getNameOrInitials(userObject, true) : '';
		} if (vid.isScreenShare && vid.srcObject) {
			const userObject = connectedUsers.find(
				(connectedUser) => connectedUser?.feedWebRTCIds?.includes(vid.id),
			);
			return userObject ? `${getNameOrInitials(userObject, true)}'s Screen` : '';
		} if (vid.profile) {
			const maskedProfile = getMaskedProfileById(vid.profile);
			if (maskedProfile) {
				return getNameOrInitials(maskedProfile, true);
			}
			const invite = invitations.find(
				(i: Invitation) => vid.profile && i.profile.gr_user === vid.profile,
			);
			return invite ? getNameOrInitials(invite.profile, true) : '';
		} if (vid.email) {
			return vid.email;
		}
		return '';
	}, [media, connectedUsers, invitations, getMaskedProfileById]);

	const toggleScreenShareDialog = () => {
		dispatch(setSharingScreen(!sharingScreen));
	};

	return {
		backstageCommsToggle,
		idsOfVideosInCanvas,
		toggleAudio,
		toggleVideo,
		toggleMuteAll,
		toggleTitles,
		titlesShowing,
		getFeedName,
		toggleScreenShareDialog,
	};
};

export default useUserActions;
